import { OrdenItem } from '../../domain/orden.domain';
import { TarifaDetalle } from '../../domain/tarifa-detalle.domain';
import { PricingCalculationTrace } from './pricing-trace.model';

export interface PricingCalculationResult {
  subtotalPreview: number;
  duracionSegundosPreview: number;
  unidadTarifacion: string;
  precioUnitario: number;
  trace: PricingCalculationTrace;
  errorMessage?: string | null;
}

export class PricingCalculator {
  calculate(
    item: Partial<OrdenItem>,
    detalle: TarifaDetalle
  ): PricingCalculationResult {
    const unidadTarifacion = String(detalle.unidadTarifacion || '').toUpperCase();
    const precioUnitario = this.parsePrecio(detalle.precioUnitario);
    const bonificado = !!item.bonificado;

    if (!unidadTarifacion) {
      return this.buildError('Unidad de tarifacion no definida.');
    }

    const baseTrace = {
      unidadTarifacion,
      inputs: {},
      billingQty: 0,
      factor: 0,
      subtotalPreview: 0,
      duracionSegundosPreview: 0,
      bonificado
    };

    if (bonificado) {
      return {
        subtotalPreview: 0,
        duracionSegundosPreview: 0,
        unidadTarifacion,
        precioUnitario,
        trace: {
          ...baseTrace,
          formula: 'Bonificado'
        },
        errorMessage: null
      };
    }

    if (unidadTarifacion === 'SEGUNDO') {
      const avisos = item.avisos;
      const duracionSegundos = item.duracionSegundos;
      if (avisos === null || avisos === undefined || duracionSegundos === null || duracionSegundos === undefined) {
        return this.buildError('Faltan avisos o duración para tarifar en segundos.');
      }
      const base = Number(avisos) * Number(duracionSegundos);
      const subtotalPreview = base * precioUnitario;
      return {
        subtotalPreview,
        duracionSegundosPreview: base,
        unidadTarifacion,
        precioUnitario,
        trace: {
          ...baseTrace,
          formula: 'avisos * duracionSegundos * precioUnitario',
          inputs: { avisos: Number(avisos), duracionSegundos: Number(duracionSegundos), precioUnitario, base },
          billingQty: base,
          factor: Number(duracionSegundos),
          subtotalPreview,
          duracionSegundosPreview: base
        },
        errorMessage: null
      };
    }

    if (unidadTarifacion === 'DIA') {
      const rangeResult = this.daysInclusive(item.fechaDesde, item.fechaHasta);
      const cantidad = item.cantidad;
      if (rangeResult.hasRange) {
        if (rangeResult.days <= 0) {
          return this.buildError('Rango de fechas inválido para tarifar por día.');
        }
      } else if (cantidad === null || cantidad === undefined || Number(cantidad) <= 0) {
        return this.buildError('Faltan fechas o cantidad para tarifar por día.');
      }
      const billingQty = rangeResult.hasRange ? rangeResult.days : Number(cantidad ?? 0);
      const subtotalPreview = billingQty * precioUnitario;
      return {
        subtotalPreview,
        duracionSegundosPreview: 0,
        unidadTarifacion,
        precioUnitario,
        trace: {
          ...baseTrace,
          formula: rangeResult.hasRange ? 'dias * precioUnitario' : 'cantidad * precioUnitario',
          inputs: { dias: rangeResult.days, cantidad: Number(cantidad ?? 0), precioUnitario },
          billingQty,
          factor: 1,
          subtotalPreview,
          duracionSegundosPreview: 0
        },
        errorMessage: null
      };
    }

    if (unidadTarifacion === 'PIEZA' || unidadTarifacion === 'PAQUETE' || unidadTarifacion === 'UNIDAD') {
      const cantidad = Number(item.cantidad || 0);
      const subtotalPreview = cantidad * precioUnitario;
      return {
        subtotalPreview,
        duracionSegundosPreview: 0,
        unidadTarifacion,
        precioUnitario,
        trace: {
          ...baseTrace,
          formula: 'cantidad * precioUnitario',
          inputs: { cantidad, precioUnitario },
          billingQty: cantidad,
          factor: 1,
          subtotalPreview,
          duracionSegundosPreview: 0
        },
        errorMessage: null
      };
    }

    return this.buildError(`Unidad de tarifacion desconocida: ${unidadTarifacion}`);
  }

  private daysInclusive(desde: any, hasta: any): { days: number; hasRange: boolean } {
    if (!desde || !hasta) {
      return { days: 0, hasRange: false };
    }
    const desdeDate = new Date(desde);
    const hastaDate = new Date(hasta);
    if (isNaN(desdeDate.getTime()) || isNaN(hastaDate.getTime())) {
      return { days: 0, hasRange: false };
    }
    desdeDate.setHours(0, 0, 0, 0);
    hastaDate.setHours(0, 0, 0, 0);
    if (hastaDate < desdeDate) {
      return { days: 0, hasRange: true };
    }
    const diff = hastaDate.getTime() - desdeDate.getTime();
    return { days: Math.floor(diff / (1000 * 60 * 60 * 24)) + 1, hasRange: true };
  }

  private parsePrecio(value: string | number | undefined): number {
    if (value === undefined || value === null) {
      return 0;
    }
    if (typeof value === 'number') {
      return value;
    }
    return parseFloat(value) || 0;
  }

  private buildError(message: string): PricingCalculationResult {
    return {
      subtotalPreview: 0,
      duracionSegundosPreview: 0,
      unidadTarifacion: '',
      precioUnitario: 0,
      trace: {
        unidadTarifacion: '',
        formula: message,
        inputs: {},
        billingQty: 0,
        factor: 0,
        subtotalPreview: 0,
        duracionSegundosPreview: 0,
        bonificado: false
      },
      errorMessage: message
    };
  }
}
